import { useState, useEffect, useRef } from 'react'

type Level = 'silent' | 'soft' | 'normal' | 'loud'

export function useMicLevel(active: boolean): Level {
  const [level, setLevel] = useState<Level>('silent')
  const audioContextRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const dataArrayRef = useRef<Uint8Array | null>(null)
  const rafIdRef = useRef<number | null>(null)
  const streamRef = useRef<MediaStream | null>(null)

  useEffect(() => {
    if (!active) {
      // when inactive, reset level and skip processing
      setLevel('silent')
      return
    }
    let cancelled = false
    async function setup() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        if (cancelled) return
        streamRef.current = stream
        const AudioCtx = window.AudioContext || (window as any).webkitAudioContext
        const audioContext = new AudioCtx()
        audioContextRef.current = audioContext
        const analyser = audioContext.createAnalyser()
        analyser.fftSize = 512
        analyserRef.current = analyser
        const source = audioContext.createMediaStreamSource(stream)
        source.connect(analyser)
        const bufferLength = analyser.frequencyBinCount
        const dataArray = new Uint8Array(bufferLength)
        dataArrayRef.current = dataArray

        const tick = () => {
          analyser.getByteTimeDomainData(dataArray)
          let sum = 0
          for (let i = 0; i < bufferLength; i++) {
            const val = (dataArray[i] - 128) / 128
            sum += val * val
          }
          const rms = Math.sqrt(sum / bufferLength)
          let newLevel: Level
          if (rms < 0.01) newLevel = 'silent'
          else if (rms < 0.05) newLevel = 'soft'
          else if (rms < 0.15) newLevel = 'normal'
          else newLevel = 'loud'
          setLevel(newLevel)
          rafIdRef.current = requestAnimationFrame(tick)
        }
        tick()
      } catch (err) {
        console.error('Error accessing microphone', err)
      }
    }
    setup()

    return () => {
      cancelled = true
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current)
      if (audioContextRef.current) audioContextRef.current.close()
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop())
        streamRef.current = null
      }
    }
  }, [active])

  return level
}
